import React from 'react';

import { useIntl } from 'react-intl';

import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { BillingEntity } from 'src/features/billing/utils/types';
import { billingMessages } from 'src/features/billing/utils/messages';
import { LIST_BILLING_ENTITIES_RESOURCE } from 'src/features/billing/api/useListBillingEntities';
import { queryClient } from 'src/lib/react-query';
import { toast } from 'src/hooks/useToast';
import { BILLING_ENTITY_RESOURCE } from 'src/features/billing/api/useGetBillingEntity';
import { usePatchBillingEntity } from 'src/features/billing/api/usePatchBillingEntity';
import { ICON_MAP } from 'src/utils/constants';
import { generalMessages } from 'src/registry/utils/messages';
import BillingEntityFormModal from 'src/features/billing/components/forms/BillingEntityFormModal';

interface UpdateBillingEntityFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedBillingEntity: BillingEntity;
    children?: React.ReactNode;
}

function UpdateBillingEntityFormModal<TData>({
    className,
    semantics,
    children,
    selectedBillingEntity,
}: UpdateBillingEntityFormModalProps<TData>) {
    const { formatMessage } = useIntl();

    const { mutateAsync, isPending } = usePatchBillingEntity({
        onSuccess: ({ name }) => {
            void queryClient.invalidateQueries({ queryKey: [LIST_BILLING_ENTITIES_RESOURCE] });
            void queryClient.invalidateQueries({ queryKey: [BILLING_ENTITY_RESOURCE] });
            toast({
                title: formatMessage(billingMessages.updateBillingEntitySuccess),
                description: formatMessage(billingMessages.updateBillingEntitySuccessDescription, {
                    entity: name,
                }),
            });
        },
    });

    return (
        <BillingEntityFormModal
            selectedBillingEntity={selectedBillingEntity}
            className={className}
            mutate={(values, onSuccess, onError) =>
                mutateAsync({ data: values, id: selectedBillingEntity.id }, { onSuccess, onError })
            }
            title={formatMessage(generalMessages.editEntry, { entry: semantics?.entryName?.toLowerCase() })}
            icon={ICON_MAP.EDIT}
            isPending={isPending}
            subtitle={formatMessage(billingMessages.editBillingEntitySubtitle, {
                entity: selectedBillingEntity.name,
            })}
        >
            {children}
        </BillingEntityFormModal>
    );
}

export default UpdateBillingEntityFormModal;
